import { Component, OnDestroy, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Subscription } from 'rxjs';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import { CompanyService } from '../../../shared/services/company/company.service';
import { CompanyResponseObject } from '../../../shared/interfaces/company.interface';
import { BuyerPureScoreScriptReportService } from './buyer-purescore-script-report.service';
import {
  MultiSelectedBuyer,
  Result,
  SelectedBuyer,
} from './buyer-purescore-script-report.interface';

@Component({
  selector: 'ps-buyer-purescore-script-report',
  templateUrl: './buyer-purescore-script-report.component.html',
  styleUrls: ['./buyer-purescore-script-report.component.css'],
})
export class BuyerPureScoreScriptReportComponent implements OnInit, OnDestroy {
  private readonly _subscriptions: Subscription = new Subscription();
  public buyers: CompanyResponseObject[] = [];
  public selectedBuyersList: Array<SelectedBuyer> = [];
  public surveyId: string = '';
  public reportData: Result[] = [];
  public columns: string[] = [];

  public datapickerOptions = {
    locale: { format: 'YYYY-MM-DD' },
    alwaysShowCalendars: false,
  };

  public dateRange = {
    start: { _d: new Date() },
    end: { _d: new Date() },
    label: '',
  };

  constructor(
    private readonly _toastr: ToasterService,
    private readonly _companyService: CompanyService,
    private readonly _reportService: BuyerPureScoreScriptReportService,
    private readonly _datepipe: DatePipe
  ) {}

  ngOnInit(): void {
    this._getAllBuyers();
  }

  private _getAllBuyers(): void {
    const payload = {
      isSpplr: false,
      isBuyr: true,
    };

    const buyersResponse$ = this._companyService
      .getAllBuyers(payload, true)
      .subscribe(
        (resp) => {
          this.buyers = (resp.companies || []).map((company: any) => ({
            ...company,
            selected: true,
          }));

          this.selectedBuyersList = this.buyers.map((buyer) => ({
            name: buyer.name,
            id: buyer.id,
          }));
        },
        (error) => {
          this._toastr.error(error?.error?.msg || 'Error fetching buyers');
        }
      );

    this._subscriptions.add(buyersResponse$);
  }

  public selectCompanies(event: Array<MultiSelectedBuyer>): void {
    this.selectedBuyersList = event.map((buyer: MultiSelectedBuyer) => ({
      name: buyer.name,
      id: buyer.id,
    }));
  }

  public selectedDate(value: any): void {
    const extractDate = (val: any) => {
      if (!val) {
        return new Date();
      }
      if (val._d) {
        return val._d;
      }
      if (val instanceof Date) {
        return val;
      }
      return new Date(val);
    };

    this.dateRange.start = { _d: extractDate(value.start) };
    this.dateRange.end = { _d: extractDate(value.end) };
  }

  private _triggerFileDownload(data: string, fileName: string): void {
    const csvFile = new File(['\uFEFF' + data], fileName, {
      type: 'text/csv;charset=utf-8;',
      lastModified: Date.now(),
    });

    const url = URL.createObjectURL(csvFile);
    const link = document.createElement('a');
    link.href = url;
    link.download = csvFile.name;
    link.click();
    URL.revokeObjectURL(url);
  }

  private _getBuyerIds(): number[] {
    return this.selectedBuyersList.map((buyer) => buyer.id);
  }

  public downloadReport(): void {
    if (!this.selectedBuyersList || this.selectedBuyersList.length === 0) {
      this._toastr.error('Please select at least one Buyer.');
      return;
    }

    const downloadPayload = {
      buyerId: this._getBuyerIds(),
      startDate:
        this._datepipe.transform(this.dateRange.start._d, 'yyyy-MM-dd') || '',
      endDate:
        this._datepipe.transform(this.dateRange.end._d, 'yyyy-MM-dd') || '',
      surveyId: this.surveyId.trim() ? this.surveyId.trim() : undefined,
      type: 'csv',
    };

    const downloadSub$ = this._reportService
      .downloadCsvReport(downloadPayload)
      .subscribe(
        (csvData: string) => {
          const fileName = `PureScore_Report_${this._datepipe.transform(
            new Date(),
            'yyyyMMdd'
          )}.csv`;
          this._triggerFileDownload(csvData, fileName);
        },
        (error) => {
          this._toastr.error(error?.error?.msg || 'Error downloading report');
        }
      );

    this._subscriptions.add(downloadSub$);
  }

  public getReport(): void {
    if (!this.selectedBuyersList || this.selectedBuyersList.length === 0) {
      this._toastr.error('Please select at least one Buyer.');
      return;
    }

    const payloadObj = {
      buyerId: this._getBuyerIds(),
      startDate:
        this._datepipe.transform(this.dateRange.start._d, 'yyyy-MM-dd') || '',
      endDate:
        this._datepipe.transform(this.dateRange.end._d, 'yyyy-MM-dd') || '',
      type: 'json' as const,
      surveyId: this.surveyId.trim() ? this.surveyId.trim() : undefined,
    };

    const reportsSubscription$ = this._reportService
      .getBuyerPureScoreReport(payloadObj)
      .subscribe(
        (reps) => {
          this.reportData = reps?.result || [];
          this.columns = reps?.columns || [];
        },
        (error) => {
          this._toastr.error(error?.error?.msg || 'Error in Report');
        }
      );

    this._subscriptions.add(reportsSubscription$);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }
}
